import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WhatsAppReducer } from "../../Reducers/Dashboard/WhatsAppReducer";
import type {
  WhatsAppAccountsResponse,
  WhatsAppConversationItem,
  WhatsAppMessageItem,
  WhatsAppCampaign,
  WhatsAppTemplate,
  WalletBalanceResponse,
  WalletTransactionItem,
  WalletPlanItem,
  RechargeOrderData,
  StudioAlbumItem,
  RedeemUserItem,
  CampaignAudiencePreview,
  WalletEstimateResponse,
} from "../../../Data/whatsapp.dto";

export interface WhatsAppState {
  loading: boolean;
  error: Record<string, string>;
  isError: boolean;
  sendingMessage: boolean;

  // Account
  whatsAppAccount: WhatsAppAccountsResponse | null;
  accountLoading: boolean;

  // Wallet
  walletBalance: WalletBalanceResponse | null;
  walletLoading: boolean;

  // Conversations
  conversations: WhatsAppConversationItem[];
  conversationsPage: number;
  conversationsHasMore: boolean;
  conversationsLoading: boolean;

  // Active conversation + messages
  activeConversation: WhatsAppConversationItem | null;
  messages: WhatsAppMessageItem[];
  messagesPage: number;
  messagesHasMore: boolean;
  messagesLoading: boolean;

  // Campaigns
  campaigns: WhatsAppCampaign[];
  activeCampaignDetail: WhatsAppCampaign | null;
  activeCampaignLoading: boolean;
  launchingCampaignId: string | null;

  // Templates
  templates: WhatsAppTemplate[];
  syncingTemplateId: string | null;
  deletingTemplateId: string | null;

  // Studio Albums
  studioAlbums: StudioAlbumItem[];
  studioAlbumsLoading: boolean;

  // Redeem Customers & Deduped Audience Preview
  redeemUsers: RedeemUserItem[];
  redeemUsersLoading: boolean;
  campaignAudience: CampaignAudiencePreview | null;
  campaignAudienceLoading: boolean;

  // Wallet Estimate
  walletEstimate: WalletEstimateResponse | null;
  walletEstimateLoading: boolean;

  // Wallet transactions
  walletTransactions: WalletTransactionItem[];
  walletTransactionsPage: number;
  walletTransactionsTotalPages: number;

  // Wallet plans
  walletPlans: WalletPlanItem[];

  // Recharge flow
  rechargeOrder: RechargeOrderData | null;
  rechargeLoading: boolean;
  rechargeVerifying: boolean;

  // Recharge order history
  rechargeOrders: RechargeOrderData[];
  rechargeOrdersPage: number;
  rechargeOrdersTotalPages: number;
  rechargeOrdersLoading: boolean;
}

const initialState: WhatsAppState = {
  loading: false,
  error: {},
  isError: false,
  sendingMessage: false,

  whatsAppAccount: null,
  accountLoading: false,

  walletBalance: null,
  walletLoading: false,

  conversations: [],
  conversationsPage: 1,
  conversationsHasMore: true,
  conversationsLoading: false,

  activeConversation: null,
  messages: [],
  messagesPage: 1,
  messagesHasMore: true,
  messagesLoading: false,

  campaigns: [],
  activeCampaignDetail: null,
  activeCampaignLoading: false,
  launchingCampaignId: null,

  templates: [],
  syncingTemplateId: null,
  deletingTemplateId: null,

  studioAlbums: [],
  studioAlbumsLoading: false,

  redeemUsers: [],
  redeemUsersLoading: false,

  campaignAudience: null,
  campaignAudienceLoading: false,

  walletEstimate: null,
  walletEstimateLoading: false,

  walletTransactions: [],
  walletTransactionsPage: 1,
  walletTransactionsTotalPages: 1,

  walletPlans: [],

  rechargeOrder: null,
  rechargeLoading: false,
  rechargeVerifying: false,

  rechargeOrders: [],
  rechargeOrdersPage: 1,
  rechargeOrdersTotalPages: 1,
  rechargeOrdersLoading: false,
};

const whatsAppSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    setActiveConversation(state, action: PayloadAction<WhatsAppConversationItem | null>) {
      state.activeConversation = action.payload;
      // Clear messages when switching conversation
      state.messages = [];
      state.messagesPage = 1;
      state.messagesHasMore = true;

      // Reset unread count for this conversation in the list
      if (action.payload) {
        const conv = state.conversations.find(c => c.customer_number === action.payload?.customer_number);
        if (conv) {
          conv.unread_count = 0;
        }
      }
    },
    receiveNewMessage(state, action: PayloadAction<WhatsAppMessageItem>) {
      const msg = action.payload;

      // Find matching conversation to update preview and increment unread count
      const conv = state.conversations.find(c => c.customer_number === msg.customer_number);
      if (conv) {
        conv.last_message = msg.text;
        conv.last_message_at = msg.created_at;

        // Only increment unread count if the message is inbound and NOT from the active conversation
        if (msg.direction === "inbound") {
          const isActive = state.activeConversation && state.activeConversation.customer_number === msg.customer_number;
          if (!isActive) {
            conv.unread_count = (conv.unread_count || 0) + 1;
          }
        }
      }

      if (state.activeConversation && state.activeConversation.customer_number === msg.customer_number) {
        const isSameApproximateTime = (t1: string, t2: string) => {
          try {
            return Math.abs(new Date(t1).getTime() - new Date(t2).getTime()) < 10000;
          } catch (e) {
            return false;
          }
        };

        const existingIndex = state.messages.findIndex(
          m => m.id === msg.id || 
          (msg.provider_message_id && m.provider_message_id === msg.provider_message_id) ||
          (m.customer_number === msg.customer_number && m.text === msg.text && isSameApproximateTime(m.created_at, msg.created_at))
        );

        if (existingIndex !== -1) {
          state.messages[existingIndex] = msg;
        } else {
          state.messages = [...state.messages, msg].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        }
      }
    },
    clearWhatsAppError(state) {
      state.error = {};
      state.isError = false;
    },
    clearWhatsAppState(state) {
      state.whatsAppAccount = null;
      state.walletBalance = null;
    },
    clearRechargeOrder(state) {
      state.rechargeOrder = null;
      state.rechargeLoading = false;
      state.rechargeVerifying = false;
    },
    clearActiveCampaignDetail(state) {
      state.activeCampaignDetail = null;
      state.activeCampaignLoading = false;
    },
    clearWalletEstimate(state) {
      state.walletEstimate = null;
      state.walletEstimateLoading = false;
    },
    clearCampaignAudience(state) {
      state.campaignAudience = null;
      state.campaignAudienceLoading = false;
    },
  },
  extraReducers: (builder) => {
    WhatsAppReducer(builder);
  },
});

export const {
  setActiveConversation,
  receiveNewMessage,
  clearWhatsAppError,
  clearWhatsAppState,
  clearRechargeOrder,
  clearActiveCampaignDetail,
  clearWalletEstimate,
  clearCampaignAudience,
} = whatsAppSlice.actions;
export default whatsAppSlice.reducer;
